// Steady-state climbing model: the power at the pedals, after drivetrain losses,
// lifts the mass, overcomes rolling resistance and pushes the air.
//   P·η = (m·g·(sin θ + Crr·cos θ) + ½·ρ·CdA·v²)·v
// Each climb is treated as one constant gradient with no wind, so the times are
// estimates for comparing builds rather than predictions of a real ride.

export const PHYSICS = {
  g: 9.81,        // m/s²
  rho: 1.15,      // kg/m³, air at ~300 m and 20 °C
  cda: .36,       // m², riding on the hoods at climbing speed
  efficiency: .976 // chain drivetrain in good condition (~2.4 % loss)
};

export const RIDER_DEFAULTS = { riderKg: 75, watts: 250 };
export const RIDER_LIMITS = { riderKg: [35, 150], watts: [50, 600] };

// Approximate figures for well-known Strava-style segments (start of the climb to
// the top, rounded from public route profiles); real segments vary by a few
// hundred metres and some tens of metres of elevation.
export const CLIMBS = [
  { id: 'arrabassada', name: 'Tibidabo', via: 'Carretera de l’Arrabassada', area: 'Barcelona', km: 6.9, gain: 350 },
  { id: 'rocacorba', name: 'Rocacorba', via: 'classic segment', area: 'Girona', km: 10, gain: 650 },
  { id: 'angels', name: 'Els Àngels', via: 'from Girona', area: 'Girona', km: 10.3, gain: 435 },
  { id: 'montserrat', name: 'Montserrat', via: 'Monistrol → Monestir', area: 'Bages', km: 9.4, gain: 530 }
];

// Rolling resistance on real asphalt. Width is a proxy for casing: up to 32 mm the
// catalogue's tyres are slick road casings (~.004); from ~45 mm they are treaded
// gravel tyres (~.005, in line with published drum tests). Between, interpolate.
export function rollingResistance(tyreWidth) {
  if (!(tyreWidth > 0)) return .0042;
  const t = Math.min(1, Math.max(0, (tyreWidth - 32) / (45 - 32)));
  return +(.004 + t * .001).toFixed(5);
}

export function gradePercent(climb) {
  return climb.gain / (climb.km * 1000) * 100;
}

// Speed (m/s) on a gradient for a total mass. The power curve is strictly
// increasing in v, so bisection always converges.
export function climbSpeed({ watts, massKg, grade, crr = .0042, physics = PHYSICS }) {
  if (!(watts > 0) || !(massKg > 0)) return 0;
  const { g, rho, cda, efficiency } = physics;
  const theta = Math.atan(grade);
  const resist = massKg * g * (Math.sin(theta) + crr * Math.cos(theta));
  const target = watts * efficiency;
  const power = v => (resist + .5 * rho * cda * v * v) * v;
  let low = 0, high = 30;
  for (let i = 0; i < 80; i++) {
    const mid = (low + high) / 2;
    if (power(mid) < target) low = mid; else high = mid;
  }
  return (low + high) / 2;
}

// Seconds to ride a climb.
export function climbTime(climb, { riderKg, bikeKg, watts, tyreWidth }) {
  const v = climbSpeed({
    watts, massKg: riderKg + bikeKg, crr: rollingResistance(tyreWidth),
    grade: climb.gain / (climb.km * 1000)
  });
  return v > 0 ? climb.km * 1000 / v : null;
}

export function climbTimes(setup, climbs = CLIMBS) {
  return climbs.map(climb => ({ ...climb, grade: gradePercent(climb), seconds: climbTime(climb, setup) }));
}

export function clampRider(value, key) {
  const [min, max] = RIDER_LIMITS[key];
  const number = Number(value);
  if (!isFinite(number) || number <= 0) return RIDER_DEFAULTS[key];
  return Math.min(max, Math.max(min, Math.round(number)));
}

// 2475.4 → "41:15"; minutes keep counting past an hour.
export function formatClimbTime(seconds) {
  if (typeof seconds !== 'number' || !(seconds >= 0)) return '–';
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

// Signed difference: "−38 s", "+1:04", "±0 s". Uses a true minus sign.
export function formatClimbDelta(seconds) {
  const total = Math.round(seconds);
  if (!total) return '±0 s';
  const sign = total < 0 ? '−' : '+';
  const abs = Math.abs(total);
  return sign + (abs < 60 ? `${abs} s` : formatClimbTime(abs));
}
